import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, ChevronRight, Star } from "lucide-react";
import { Logo } from "./Logo";
import {
  PHONE, PHONE_LINK, EMAIL, FACEBOOK_URL, INSTAGRAM_URL, GOOGLE_REVIEW_URL,
  FONT_HEAD, FONT_BODY, NAVY, GOLD, isSpanishPath,
} from "../lib/constants";
import { SERVICE_LINKS, AREA_LINKS } from "../lib/localPages";

const FOOTER_LINKS = [
  { label: "Services", to: "/services" },
  { label: "Post Construction", to: "/services/post-construction-cleaning-naples-fl" },
  { label: "About Us", to: "/about" },
  { label: "Gallery", to: "/gallery" },
  { label: "Blog", to: "/blog" },
  { label: "Free Estimate", to: "/estimate" },
  { label: "Contact", to: "/contact" },
];

const FOOTER_LINKS_ES = [
  { label: "Inicio", to: "/es", testid: "footer-link-inicio" },
  { label: "Limpieza post-construcción", to: "/services/post-construction-cleaning-naples-fl", testid: "footer-link-post-construction" },
  { label: "Nosotros", to: "/about", testid: "footer-link-about-us" },
  { label: "Galería", to: "/gallery", testid: "footer-link-gallery" },
  { label: "Blog", to: "/blog", testid: "footer-link-blog" },
  { label: "Cotización gratis", to: "/es/estimate", testid: "footer-link-free-estimate" },
  { label: "Contacto", to: "/contact", testid: "footer-link-contact" },
];

const HEADINGS = {
  en: { quick: "Quick Links", services: "Services", areas: "Areas", contact: "Contact Us" },
  es: { quick: "Enlaces rápidos", services: "Servicios", areas: "Zonas", contact: "Contacto" },
};

const useFooterLang = () => (isSpanishPath(useLocation().pathname) ? "es" : "en");

const SOCIALS = [
  { Icon: Facebook, href: FACEBOOK_URL, testid: "social-facebook", aria: "Facebook — Cindyrella Cleaning" },
  { Icon: Instagram, href: INSTAGRAM_URL, testid: "social-instagram", aria: "Instagram — @cindyrella_cleaning" },
];

const FooterHeading = ({ children }) => (
  <h4 className="text-sm font-bold mb-5 uppercase tracking-widest" style={{ color: GOLD, fontFamily: FONT_HEAD }}>
    {children}
  </h4>
);

const FooterBrand = () => {
  const lang = useFooterLang();
  return (
  <div>
    <Logo theme="dark" />
    <p className="text-sm mt-4 leading-relaxed" style={{ color: "rgba(255,255,255,0.65)", fontFamily: FONT_BODY }}>
      Naples' trusted cleaning service for homes, vacation rentals,
      restaurants, and offices.
    </p>
    <div className="flex gap-3 mt-6">
      {SOCIALS.map(({ Icon, href, testid, aria }) => (
        <a
          key={testid}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={testid}
          className="w-9 h-9 flex items-center justify-center transition-all duration-200 hover:scale-110"
          style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.1)" }}
          aria-label={aria}
        >
          <Icon size={16} style={{ color: GOLD }} />
        </a>
      ))}
    </div>
    <a
      href={GOOGLE_REVIEW_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="footer-google-review"
      className="inline-flex items-center gap-2 mt-6 px-4 py-2.5 text-xs font-bold transition-all duration-200 hover:scale-[1.03]"
      style={{ background: GOLD, color: NAVY, fontFamily: FONT_BODY }}
    >
      <Star size={14} fill={NAVY} />
      {lang === "es" ? "Déjanos una reseña en Google" : "Leave us a Google Review"}
    </a>
  </div>
  );
};

const FooterContact = () => {
  const lang = useFooterLang();
  return (
  <div>
    <FooterHeading>{HEADINGS[lang].contact}</FooterHeading>
    <div className="space-y-3">
      <a
        href={PHONE_LINK}
        data-testid="footer-phone"
        className="flex items-center gap-3 text-sm transition-colors hover:text-[#D4A843]"
        style={{ color: "rgba(255,255,255,0.8)", fontFamily: FONT_BODY }}
      >
        <Phone size={15} style={{ color: GOLD, flexShrink: 0 }} />
        {PHONE}
      </a>
      <a
        href={`mailto:${EMAIL}`}
        data-testid="footer-email"
        className="flex items-center gap-3 text-sm transition-colors hover:text-[#D4A843]"
        style={{ color: "rgba(255,255,255,0.8)", fontFamily: FONT_BODY }}
      >
        <Mail size={15} style={{ color: GOLD, flexShrink: 0 }} />
        {EMAIL}
      </a>
      <div className="flex items-center gap-3 text-sm" style={{ color: "rgba(255,255,255,0.8)", fontFamily: FONT_BODY }}>
        <MapPin size={15} style={{ color: GOLD, flexShrink: 0 }} />
        Naples, FL — Collier County
      </div>
    </div>
  </div>
  );
};

const FooterLinkList = ({ heading, links, prefix }) => (
  <div>
    <FooterHeading>{heading}</FooterHeading>
    <div className="space-y-2">
      {links.map(({ label, to }) => (
        <Link
          key={to}
          to={to}
          data-testid={`${prefix}-${to.split("/").pop()}`}
          className="flex items-center gap-1.5 text-sm transition-colors hover:text-[#D4A843]"
          style={{ color: "rgba(255,255,255,0.8)", fontFamily: FONT_BODY }}
        >
          <ChevronRight size={13} style={{ color: GOLD }} />
          {label}
        </Link>
      ))}
    </div>
  </div>
);

const FooterQuickLinks = () => {
  const lang = useFooterLang();
  const links = lang === "es" ? FOOTER_LINKS_ES : FOOTER_LINKS;
  return (
  <div>
    <FooterHeading>{HEADINGS[lang].quick}</FooterHeading>
    <div className="space-y-2">
      {links.map(({ label, to, testid }) => (
        <Link
          key={to}
          to={to}
          data-testid={testid || `footer-link-${label.toLowerCase().replace(/\s/g, "-")}`}
          className="flex items-center gap-2 text-sm w-full text-left transition-colors hover:text-[#D4A843]"
          style={{ color: "rgba(255,255,255,0.7)", fontFamily: FONT_BODY }}
        >
          <ChevronRight size={13} style={{ color: GOLD }} />
          {label}
        </Link>
      ))}
    </div>
  </div>
  );
};

const GovContractsLink = () => {
  const { pathname } = useLocation();
  const isSpanish = isSpanishPath(pathname);
  return (
    <Link to="/government-contracts" data-testid="footer-government-contracts" className="text-xs transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.45)", fontFamily: FONT_BODY }}>
      {isSpanish ? "Contratos Gubernamentales" : "Government Contracts"}
    </Link>
  );
};

const FooterBottomBar = () => {
  const lang = useFooterLang();
  return (
  <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
      <p className="text-xs text-center md:text-left" style={{ color: "rgba(255,255,255,0.45)", fontFamily: FONT_BODY }}>
        Cindyrella Cleaning LLC — Naples, FL | {lang === "es" ? "Con licencia y seguro" : <>Licensed &amp; Insured</>}
      </p>
      <div className="flex flex-wrap justify-center md:justify-end gap-x-5 gap-y-2">
        <Link to="/privacy" data-testid="footer-privacy" className="text-xs transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.45)", fontFamily: FONT_BODY }}>
          {lang === "es" ? "Política de privacidad" : "Privacy Policy"}
        </Link>
        <Link to="/terms" data-testid="footer-terms" className="text-xs transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.45)", fontFamily: FONT_BODY }}>
          {lang === "es" ? "Términos del servicio" : "Terms of Service"}
        </Link>
        <Link to="/non-discrimination" data-testid="footer-non-discrimination" className="text-xs transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.45)", fontFamily: FONT_BODY }}>
          {lang === "es" ? "No discriminación" : "Non-Discrimination"}
        </Link>
        <Link to="/accessibility" data-testid="footer-accessibility" className="text-xs transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.45)", fontFamily: FONT_BODY }}>
          Accessibility
        </Link>
        <Link to="/safety" data-testid="footer-safety" className="text-xs transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.45)", fontFamily: FONT_BODY }}>
          Safety &amp; Insurance
        </Link>
        <GovContractsLink />
      </div>
    </div>
  </div>
  );
};

const FooterColumns = () => {
  const lang = useFooterLang();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
      <div className="sm:col-span-2 lg:col-span-1"><FooterBrand /></div>
      <FooterLinkList heading={HEADINGS[lang].services} links={SERVICE_LINKS[lang]} prefix="footer-service" />
      <FooterLinkList heading={HEADINGS[lang].areas} links={AREA_LINKS[lang]} prefix="footer-area" />
      <FooterQuickLinks />
      <FooterContact />
    </div>
  );
};

export const Footer = () => (
  <footer id="footer" data-testid="footer" style={{ background: NAVY }}>
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
      <FooterColumns />
    </div>
    <FooterBottomBar />
  </footer>
);
